// Tiny module store so only one visitor screen is on screen at a time (Visitor Flows
// rules 1 & 2). A visible VisitorDetailsModal registers the visit it shows; the ring
// popup (GlobalVisitorApprovalOverlay) and the in-app banner read it.
//
//   - same visit rings      → overlay calls refresh(): the open sheet refetches silently
//                             and shows its Approve/Deny bar; no popup on top.
//   - different visit rings → overlay calls close(): every open sheet closes itself,
//                             then the popup opens.
//   - GlobalPushBanner skips a visitor banner for the visit that's already open.

export interface OpenVisit {
    visitId: number | null;
    visitorUuid: string | null;
}

export type VisitorSheetEvent =
    | { type: 'closeAll' }
    | { type: 'refresh'; visit: OpenVisit };

type Listener = (e: VisitorSheetEvent) => void;

let current: { owner: object; visit: OpenVisit } | null = null;
const listeners = new Set<Listener>();

const toId = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/** True when `visit` names the open sheet's visit (by visitId when both have one, else by uuid). */
const matches = (open: OpenVisit | null, visit: { visitId?: unknown; visitorUuid?: unknown }): boolean => {
    if (!open) return false;
    const id = toId(visit.visitId);
    if (id !== null && open.visitId !== null) return id === open.visitId;
    const uuid = visit.visitorUuid ? String(visit.visitorUuid) : null;
    return !!uuid && uuid === open.visitorUuid;
};

export const visitorSheetBus = {
    /** The visit shown by the open details sheet, or null. */
    get openVisit(): OpenVisit | null {
        return current ? current.visit : null;
    },
    /** Register (or update) the visit an open sheet shows. `owner` is the sheet's own token. */
    setOpen(owner: object, visit: OpenVisit) {
        current = { owner, visit: { visitId: toId(visit.visitId), visitorUuid: visit.visitorUuid || null } };
    },
    /** Unregister; a no-op when another sheet has registered since. */
    clear(owner: object) {
        if (current?.owner === owner) current = null;
    },
    isOpen(visit: { visitId?: unknown; visitorUuid?: unknown }): boolean {
        return matches(current?.visit ?? null, visit);
    },
    /** Ask every open sheet to close. */
    close() {
        listeners.forEach(l => l({ type: 'closeAll' }));
    },
    /** Ask the sheet showing `visit` to refetch silently. */
    refresh(visit: OpenVisit) {
        listeners.forEach(l => l({ type: 'refresh', visit }));
    },
    subscribe(l: Listener): () => void {
        listeners.add(l);
        return () => { listeners.delete(l); };
    },
    matches,
};
